import React, {useState, useEffect} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PermissionsInfoIcon from './PermissionsInfoIcon';

import {generalStore, history} from '../../../index';
import {routes} from '../../../App';
import {RolePresetsRequester} from '../../Domain/Groups/RolePresetsRequester';
import {FrontlineCheckbox} from '../../Shared/FrontlineCheckbox';
import PermissionsModal from './PermissionsModal';

const PresetItem = ({preset, isSelected, isMulti, onClick, onInfo}) => (
    <div
        data-testid={`preset-list-item-${preset.Name}`}
        className={`group flex items-center gap-2 px-3 py-2 rounded-menu cursor-pointer hover:bg-panels-normal ${isSelected ? 'bg-nav-selected' : ''}`}
        onClick={onClick}
    >
        {isMulti && <FrontlineCheckbox isChecked={isSelected} onChange={() => {}} size={18} isWithPadding={false} />}
        <div className="flex-1 flex flex-col gap-0.5">
            <span className="text-sm font-medium text-main-normal">{preset.Name}</span>
            {preset.Description && <span className="text-xs text-main-light">{preset.Description}</span>}
        </div>
        <PermissionsInfoIcon name={preset.Name} size={16} zIndex={2100} onClick={(e) => { e.stopPropagation(); onInfo(preset); }} />
    </div>
);

let cachedPresets = null;

export const clearPresetsCache = () => { cachedPresets = null; };

const PresetListView = ({onSelect, onFilterApply, onSwitchToManual, onClose}) => {
    const [presets, setPresets] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [isLoading, setIsLoading] = useState(true);
    const [infoPreset, setInfoPreset] = useState(null);
    const isMulti = !!onFilterApply;

    useEffect(() => {
        const fetchPresets = async () => {
            setSelectedIds(new Set());
            if (cachedPresets) {
                setPresets(cachedPresets);
                setIsLoading(false);
                return;
            }
            setIsLoading(true);
            try {
                const data = await RolePresetsRequester.getRolePresets(generalStore.workspace);
                setPresets(data || []);
                cachedPresets = data || [];
            } catch (error) {
                console.error('Failed to fetch presets:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPresets();
    }, []);

    const handleClick = (preset) => {
        if (isMulti) {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.has(preset.RolePresetId) ? next.delete(preset.RolePresetId) : next.add(preset.RolePresetId);
                return next;
            });
        } else if (onSelect) {
            onSelect(preset);
        }
    };

    const handleApply = () => {
        const selected = presets.filter((p) => selectedIds.has(p.RolePresetId));
        onFilterApply(selected);
    };

    return (
        <>
            <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{isMulti ? 'Filter by Role' : 'Assign Role'}</span>
                <div className="flex items-center gap-1">
                    <TuneIcon data-testid="preset-switch-to-manual" style={{fontSize: 18}} className="text-link-normal cursor-pointer hover:text-main-normal" onClick={onSwitchToManual} />
                    <CloseIcon style={{fontSize: 18}} className="text-main-light cursor-pointer hover:text-main-normal" onClick={onClose} />
                </div>
            </div>
            <div className="flex flex-col gap-0.5 overflow-y-auto" style={{maxHeight: 250}}>
                {isLoading && <div className="px-2 py-2 text-xs text-main-light">Loading...</div>}
                {!isLoading && presets.map((preset) => (
                    <PresetItem
                        key={preset.RolePresetId}
                        preset={preset}
                        isSelected={isMulti ? selectedIds.has(preset.RolePresetId) : false}
                        isMulti={isMulti}
                        onClick={() => handleClick(preset)}
                        onInfo={setInfoPreset}
                    />
                ))}
                {!isLoading && presets.length === 0 && (
                    <div className="px-2 py-2 text-xs text-main-light">No roles defined yet.</div>
                )}
            </div>

            {isMulti ? (
                <div className="flex items-center justify-end gap-2 px-1 pt-2" style={{borderTop: '1px solid var(--panels-normal-color)'}}>
                    <div data-testid="preset-filter-cancel" className="px-3 py-1.5 rounded-button text-sm font-medium text-main-normal cursor-pointer hover:bg-panels-normal" onClick={onClose}>Cancel</div>
                    <div
                        data-testid="preset-filter-apply"
                        className={`px-3 py-1.5 rounded-button text-sm font-medium text-classic-button-normal-text ${selectedIds.size ? 'bg-classic-button-normal cursor-pointer hover:bg-classic-button-hover' : 'bg-classic-button-disabled cursor-default'}`}
                        onClick={selectedIds.size ? handleApply : undefined}
                    >
                        Apply ({selectedIds.size})
                    </div>
                </div>
            ) : (
                <div
                    data-testid="preset-manage-roles"
                    className="flex items-center gap-1.5 px-1 py-2 text-sm text-link-normal cursor-pointer hover:underline"
                    style={{borderTop: '1px solid var(--panels-normal-color)'}}
                    onClick={() => { onClose(); history.push(routes.ROLES); }}
                >
                    <SettingsOutlinedIcon style={{fontSize: 16}} />
                    Manage Roles
                </div>
            )}
            {infoPreset && (
                <PermissionsModal
                    open={!!infoPreset}
                    title={`${infoPreset.Name} Permissions`}
                    trainingRole={infoPreset.TrainingAndFieldRole}
                    contentRole={infoPreset.ContentCreationRole}
                    adminRole={infoPreset.AdministrationRole}
                    onClose={() => setInfoPreset(null)}
                />
            )}
        </>
    );
};

export default PresetListView;
